/**
 * ConnectivityBanner — the ONE place the app tells the merchant that the
 * backend is unreachable, that they're being rate-limited, or that the
 * Shopify session is gone. Driven by the events lib/api.js raises from
 * authenticatedFetch, so no page has to remember to handle them.
 *
 * Mounted once in AppLayout above the routed content.
 */
import React, { useEffect, useState } from 'react';
import { Banner, BlockStack, Button, Text } from '@shopify/polaris';
import {
  API_OFFLINE_EVENT, API_ONLINE_EVENT, API_RATE_LIMITED_EVENT, SESSION_LOST_EVENT,
} from '../lib/api';

export default function ConnectivityBanner({ reinstallUrl }) {
  const [offline, setOffline] = useState(false);
  const [rateLimited, setRateLimited] = useState(null);
  const [sessionLost, setSessionLost] = useState(null);

  useEffect(() => {
    const onOffline = () => setOffline(true);
    const onOnline = () => setOffline(false);
    let rlTimer = null;
    const onRateLimited = (e) => {
      const retryAfter = Number(e?.detail?.retryAfter) || 0;
      setRateLimited({ retryAfter });
      if (rlTimer) clearTimeout(rlTimer);
      rlTimer = setTimeout(() => setRateLimited(null), Math.max(6000, Math.min(retryAfter * 1000, 30000)));
    };
    const onSessionLost = (e) => setSessionLost({ reinstallUrl: e?.detail?.reinstallUrl || reinstallUrl });
    // Browser-level offline/online too — cheaper than waiting for a fetch to fail.
    const onBrowserOffline = () => setOffline(true);
    const onBrowserOnline = () => setOffline(false);

    window.addEventListener(API_OFFLINE_EVENT, onOffline);
    window.addEventListener(API_ONLINE_EVENT, onOnline);
    window.addEventListener(API_RATE_LIMITED_EVENT, onRateLimited);
    window.addEventListener(SESSION_LOST_EVENT, onSessionLost);
    window.addEventListener('offline', onBrowserOffline);
    window.addEventListener('online', onBrowserOnline);
    return () => {
      window.removeEventListener(API_OFFLINE_EVENT, onOffline);
      window.removeEventListener(API_ONLINE_EVENT, onOnline);
      window.removeEventListener(API_RATE_LIMITED_EVENT, onRateLimited);
      window.removeEventListener(SESSION_LOST_EVENT, onSessionLost);
      window.removeEventListener('offline', onBrowserOffline);
      window.removeEventListener('online', onBrowserOnline);
      if (rlTimer) clearTimeout(rlTimer);
    };
  }, [reinstallUrl]);

  if (!offline && !rateLimited && !sessionLost) return null;

  return (
    <div style={{ padding: '12px 16px 0' }}>
      <BlockStack gap="200">
        {sessionLost && (
          <Banner tone="critical">
            <BlockStack gap="200">
              <Text as="p">
                Your Shopify session has expired or isn&apos;t connected. Reconnect to keep using Autonoma.
              </Text>
              <div>
                <Button variant="primary" url={sessionLost.reinstallUrl} external>
                  Reconnect Shopify
                </Button>
              </div>
            </BlockStack>
          </Banner>
        )}
        {offline && (
          <Banner tone="warning">
            <Text as="p">
              We can&apos;t reach Autonoma right now. Check your connection — we&apos;ll reconnect automatically.
            </Text>
          </Banner>
        )}
        {rateLimited && !offline && (
          <Banner tone="info">
            <Text as="p">
              You&apos;re sending requests a little fast. Give it
              {rateLimited.retryAfter ? ` ${rateLimited.retryAfter} seconds` : ' a moment'} and try again.
            </Text>
          </Banner>
        )}
      </BlockStack>
    </div>
  );
}
